import { inject } from '@angular/core';

import {
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithElementSearchAndFilters,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { AuthService } from '@blaze-shared/auth-data-access';
import {
  type CanvasElementPropertiesWithData,
  DEFAULT_ELEMENT_PADDING,
  DEFAULT_NO_DATA_MESSAGE,
  type ElementSizingMode,
  forcedCustomTemplateLayoutMode,
  getBorderColor,
  IMAGE_SHAPE_OPTIONS,
  type ImageShape,
  ITEMS_CONTAINER_ACTION_ADD,
  LIST_DIRECTION_MODES_OPTIONS,
  type ListDirectionMode,
  ListScrollMode,
  type PropertiesConfig,
  type PropertiesConfigAction,
  type PropertiesConfigInputContainerItemActionModel,
  type PropertiesConfigInputContainerItemUnifiedBulkActionModel,
  type PropertiesConfigInputContainerItemUnifiedRowActionModel,
  type PropertiesConfigInputListLayoutModel,
  type PropertiesConfigInputOptionsToggle,
  type PropertiesConfigItemDefinition,
  type PropertiesConfigSubsection,
  SearchAndFiltersMode,
  type WithUnifiedBulkActions,
} from '@blaze-shared/elements-domain';
import { COLUMN_ID_OR_NAME, DEFAULT_SCHEMA } from '@blaze-shared/schema-domain';
import { isNullish } from '@blaze-shared/shared-util';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { ListDefinitionMapper } from './ai/list.definition-mapper';
import { ListElementHandler } from './ai/list.element-handler';
import { ListComponent } from './list.component';
import {
  type ListPropsModel,
  type PropertiesConfigInputListConditionalFormatModel,
} from './list.models';
import { ListPropsBuilder } from './list.props-builder';
import { ListPropsExtractor } from './list.props-extractor';
import {
  customFieldLabelPositionForDirection,
  resolveListImagePadding,
  resolveListTemplateImage,
} from './list.template-appearance';
import { listV2Migration } from './migrations/002-list.migration';
import { listV3Migration } from './migrations/003-list.migration';

export type ListDef = WithProps<ListPropsModel> & WithElementSearchAndFilters & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    list: ListDef;
  }
}

const DEFAULT_WIDTH = 600;
const DEFAULT_HEIGHT = 400;
const MIN_SIZE = 300;
const MAX_WIDTH = 900;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];

registerElementDefinition({
  type: 'list',
  componentType: ListComponent,
  isListed: true,
  name: 'List',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  propsBuilder: ListPropsBuilder,
  propsExtractor: ListPropsExtractor,
  defaults: getDefaults(),
  initial: getInitial(),
  tags: ['Table', 'Notification'],
  styleIncludePaths: [
    'style',
    'content.title.align',
    'content.title.iconPosition',
    'content.indicator.icon',
    'content.indicator.align',
  ],
  metaUiFeatures: {
    type: 'container',
    paths: {
      columns: 'content.customFields',
    },
  },
  features: {
    addRelatedWidgetSupport: true,
    loadingIndication: 'fadeWithIndicator',
    visualStyle: 'block',
    ai: {
      showAiPromptAutomatically: true,
      description: true,
    },
  },
  ai: {
    name: 'list',
    handler: () => ListElementHandler,
    definitionMapper: () => ListDefinitionMapper,
  },
  version: 3,
  migrations: {
    2: listV2Migration,
    3: listV3Migration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        isQueryable: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      { eventType: 'row-select', schema: DEFAULT_SCHEMA },
      { eventType: 'query', schema: null },
      { eventType: 'action', schema: DEFAULT_SCHEMA }, // schema depends on the action. overridden in list.component
    ],
  };
}

function getDefaults(): PropsOf<'list'> {
  return {
    content: {
      columns: [],
      fields: {},
      customFields: [],
      actions: {
        align: 'left',
        disableActionWhenPending: true,
        items: [],
        mode: 'always',
      },
      searchAndFilters: {
        mode: SearchAndFiltersMode.PREDEFINED,
        filters: [],
      },
      list: {
        actionsView: 'row',
        bulkActionsDisplayMode: 'always',
        layout: {
          direction: 'vertical',
          mode: 'vscroll',
          cols: 3,
          rows: 3,
        },
      },
      subscriptionPlan: {
        bulletIcon: 'asterisk',
      },
      sort: {
        field: '',
        direction: '',
      },
      itemAdd: { enabled: false, label: '' },
      itemView: { enabled: false },
      itemEdit: { enabled: false },
      itemDelete: { enabled: false },
      bulkActions: [],
      bulkExport: { enabled: false },
      bulkDelete: { enabled: false },
      bulkEdit: { enabled: false },
      unifiedRowActions: [],
      unifiedBulkActions: [],
      title: {
        label: 'List',
        align: 'left',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      noDataMessage: DEFAULT_NO_DATA_MESSAGE,
      mobile: {
        scrollMode: ListScrollMode.Swipe,
      },
      conditionalFormatting: {
        enabled: false,
        formats: [blankListFormat()],
      },
      indicator: {
        enabled: false,
        icon: 'fas-chevron-right',
        align: { horizontal: 'right', vertical: 'middle' },
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      general: {
        heading: { color1: '$t.title', selectorType: 'solid' },
        description: { color1: '$t.value', selectorType: 'solid' },
        actions: { color1: '$t.value', selectorType: 'solid' },
        accent: { color1: '$t.accent', selectorType: 'solid' },
      },
      titleBlock: {
        fontStyle: '$t.headerFontStyle' as any,
        fontSize: '$t.headerFontSize',
        fontFamily: '$t.headerFontFamily',
        fontWeight: '$t.headerFontWeight',
        iconSize: '$t.headerIconSize',
      },
      container: {
        outlineWidth: '$t.containerOutlineWidth',
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
        radius: '$t.radius',
        fill: { color1: '$t.fill', selectorType: 'solid' },
      },
      background: {
        fill: { color1: '$t.fillInternal', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: 'solid' },
        outlineWidth: '1px',
        radius: '$t.radius',
        visualEffect: {
          type: '$t.effectType',
          data: {
            x: '$t.effectX',
            y: '$t.effectY',
            blur: '$t.effectBlur',
            color1: '$t.effectColor1',
          },
        },
      },
      list: {
        offset: {
          margin: '$t.spacingExternal',
          padding: DEFAULT_ELEMENT_PADDING,
        },
        selectedRow: { color1: '$t.accent_a10', selectorType: 'solid' },
        hover: { color1: '$t.complementary', selectorType: 'solid' },
      },
      image: {
        outline: { color1: '$t.outline', selectorType: 'none' },
        outlineWidth: '0px',
        radius: '0px',
        padding: '0px',
        behavior: 'cover',
      },
      indicator: {
        color: { color1: '$t.title', selectorType: 'solid' },
        size: '24px',
      },
    },
  };
}

function getInitial(): PartialPropsOf<'list'> {
  return {
    content: {
      searchAndFilters: { injectToElement: true },
    },
    style: {
      container: {
        fill: { color1: '$t.fill', selectorType: 'solid' },
        radius: '$t.radius',
      },
      list: {
        offset: {
          padding: '$t.spacingInternal',
        },
      },
      image: {
        shape: 'landscape-short',
      },
    },
    featuresConfig: {
      fieldsLabelGen: 2,
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  const imagePropertiesAction: PropertiesConfigAction = {
    key: 'OBJECT_EDIT_FIELD',
    paneTitle: 'Edit Image',
    config: () => [
      epc.subsections.subsection(
        'style.image',
        '',
        [
          ...epc.fields.imagePropertiesFields(
            'style.image',
            undefined,
            IMAGE_SHAPE_OPTIONS.filter((o) => o.value !== 'auto'),
            (model: PropsOf<'list'>, shape: ImageShape | undefined) => {
              /**
               * Update padding based on list direction and chosen shape
               */
              const direction = model.content.list?.layout.direction;
              const newPadding = resolveListImagePadding(direction, shape);
              return !isNullish(newPadding) ? { 'style.image.padding': newPadding } : {};
            },
          ),
          epc.utils.conditionalField(
            epc.fields.sliderField('style.image.padding', 'Padding', [
              { min: 0, max: 24, unit: 'px' },
            ]),
            'style.image.shape',
            (shape?: ImageShape) => shape !== 'circle' && shape !== 'square',
          ),
        ],
        { hideLabel: true, labelWidth: '35%' },
      ),
    ],
  };

  return epc.initConfig('list', [
    epc.section('content', 'Content', [
      epc.subsections.dataSubsection(
        'data.?key:value.enabled',
        'Fields',
        [],
        [
          epc.fields.selectDataSourceField({
            key: 'value',
            label: 'Data source',
            sources: 'list',
            bindings: [],
          }),
        ],
        { enabledByDefault: true },
      ),

      epc.subsections.subsection(
        'content',
        'Template',
        [
          epc.utils.observedField(
            epc.fields.selectFieldFromImageList(
              'list-template',
              'content.list.layout.direction',
              'Template',
              LIST_DIRECTION_MODES_OPTIONS,
              {
                allowNone: false,
                showSelection: true,
                hideLabel: true,
                optionsFilter: (_model, options) => {
                  const authService = inject(AuthService);

                  return authService.isCurrentUserAccountRootOrDev()
                    ? options
                    : options.filter((option) => option.value !== 'custom');
                },
              },
            ),
            (
              model: ListPropsModel,
              direction: ListDirectionMode,
              syncModel,
            ): Record<string, any> => {
              const oldDirection = model.content.list.layout.direction;
              if (oldDirection === direction) {
                return {};
              }

              const setLabelPosition = (oldValue: string, value: string): Record<string, string> =>
                Object.fromEntries(
                  model.content.customFields.flatMap((field, i) =>
                    field.labelBehavior === oldValue
                      ? [[`content.customFields.${i}.labelBehavior`, value]]
                      : [],
                  ),
                );
              const hasAction = (labelLowerCase: string): boolean =>
                model.content.actions.items.some(
                  ({ label }) => label.toLowerCase() === labelLowerCase,
                );
              const addAction = (label: string): Record<string, any> => ({
                [`content.actions.items.${model.content.actions.items.length}`]: { label },
              });
              const enableDetails = (value: boolean): Record<string, any> => ({
                'content.itemView.enabled': value,
              });

              const byDirection: Partial<Record<ListDirectionMode, () => Record<string, any>>> = {
                'e-commerce': () => ({
                  ...(hasAction('buy') ? {} : addAction('Buy')),
                  ...enableDetails(true),
                }),
                'subscription-plan': () => ({
                  ...(hasAction('subscribe') ? {} : addAction('Subscribe')),
                  ...enableDetails(false),
                }),
              };

              const oldLabelPosition = customFieldLabelPositionForDirection(oldDirection);
              const labelPosition = customFieldLabelPositionForDirection(direction);
              const resolvedMode = forcedCustomTemplateLayoutMode(
                direction,
                model.content.list.layout.mode,
              );

              return {
                ...(byDirection[direction]?.() ?? {}),
                ...(oldLabelPosition !== labelPosition
                  ? setLabelPosition(oldLabelPosition, labelPosition)
                  : {}),
                ...(resolvedMode ? { 'content.list.layout.mode': resolvedMode } : {}),
                'style.image': resolveListTemplateImage(direction, {
                  currentImage: model.style.image,
                  backgroundRadius: model.style.background.radius,
                  elementOutlineColor: getBorderColor(syncModel?.style?.background?.outline, ''),
                }),
              };
            },
          ),
          ...epc.utils.disabledFields(
            [
              epc.utils.conditionalField(
                epc.fields.listFieldsField('content.columns', 'data.?key:value.dataSource'),
                'content.list.layout.direction',
                (direction: ListDirectionMode) => direction === 'custom',
              ),
              ...epc.utils.conditionalFields(
                [
                  epc.fields.selectFieldFromModel(
                    'content.fields.image',
                    'Image',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                      detailsAction: imagePropertiesAction,
                    },
                  ),
                  epc.fields.selectFieldFromModel(
                    'content.fields.heading',
                    'Heading',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    },
                  ),
                  epc.fields.selectFieldFromModel(
                    'content.fields.description',
                    'Description',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    },
                  ),
                ],
                'content.list.layout.direction',
                (direction: ListDirectionMode) =>
                  direction !== 'horizontal' && direction !== 'vertical',
              ),
              ...epc.utils.conditionalFields(
                [
                  epc.fields.selectFieldFromModel(
                    'content.fields.image',
                    'Image',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                      detailsAction: imagePropertiesAction,
                    },
                  ),
                  epc.fields.selectFieldFromModel(
                    'content.fields.name',
                    'Name',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    },
                  ),
                  epc.fields.selectFieldFromModel(
                    'content.fields.message',
                    'Message',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    },
                  ),
                  epc.fields.selectFieldFromDataBinding(
                    'content.fields.isUnread',
                    'Is unread',
                    'value',
                    {
                      showSelection: true,
                      allowNone: true,
                      dataBindingsMode: 'first-dataset',
                      optionsFilter: epc.utils.columnDataTypeFilter('Boolean'),
                    },
                  ),
                  epc.fields.selectFieldFromModel(
                    'content.fields.timestamp',
                    'Timestamp',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('date', 'datetime'),
                    },
                  ),
                ],
                'content.list.layout.direction',
                (direction: ListDirectionMode) => direction !== 'notification',
              ),
              ...epc.utils.conditionalFields(
                [
                  epc.fields.selectFieldFromModel(
                    'content.fields.image',
                    'Image',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                      detailsAction: imagePropertiesAction,
                    },
                  ),
                  epc.fields.selectFieldFromModel(
                    'content.fields.title',
                    'Title',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    },
                  ),
                  epc.fields.selectFieldFromModel(
                    'content.fields.price',
                    'Price',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    },
                  ),
                  epc.fields.selectFieldFromModel(
                    'content.fields.bulletList',
                    'Bullet list',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    },
                  ),
                  epc.fields.iconField('content.subscriptionPlan.bulletIcon', 'Bullet icon'),
                ],
                'content.list.layout.direction',
                (direction: ListDirectionMode) => direction !== 'subscription-plan',
              ),
              ...epc.utils.conditionalFields(
                [
                  epc.fields.selectFieldFromModel(
                    'content.fields.image',
                    'Image',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                      detailsAction: imagePropertiesAction,
                    },
                  ),
                  epc.fields.selectFieldFromModel(
                    'content.fields.itemName',
                    'Name',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    },
                  ),
                  epc.fields.selectFieldFromModel(
                    'content.fields.itemDescription',
                    'Description',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    },
                  ),
                  epc.fields.selectFieldFromModel(
                    'content.fields.itemPrice',
                    'Price',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    },
                  ),
                  epc.fields.selectFieldFromModel(
                    'content.fields.originalPrice',
                    'Original Price',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    },
                  ),
                  epc.fields.selectFieldFromModel(
                    'content.fields.rating',
                    'Rating',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    },
                  ),
                  epc.fields.selectFieldFromModel(
                    'content.fields.ratingVotes',
                    'Rating votes',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    },
                  ),
                  epc.fields.selectFieldFromModel(
                    'content.fields.badge',
                    'Badge',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    },
                  ),
                  epc.fields.selectFieldFromModel(
                    'content.fields.callout',
                    'Call-out',
                    'content.columns',
                    {
                      showSelection: true,
                      allowNone: true,
                      labelField: 'name',
                      valueField: COLUMN_ID_OR_NAME,
                      optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    },
                  ),
                ],
                'content.list.layout.direction',
                (direction: ListDirectionMode) => direction !== 'e-commerce',
              ),
              epc.utils.conditionalField(
                epc.utils.separatedField(
                  epc.fields.listCustomFieldsField('content.customFields', 'Add Custom Field'),
                  { hideSeparatorWhenHidden: true },
                ),
                'content.list.layout.direction',
                (direction: ListDirectionMode) => direction === 'custom',
              ),
              epc.utils.conditionalField(
                epc.fields.customTemplateField('content.list.customTemplate'),
                'content.list.layout.direction',
                (direction: ListDirectionMode) => direction !== 'custom',
              ),
            ],
            (model) =>
              !getValueByPath(model, 'data.?key:value.dataSource')
                ? 'The Value of the element is not bound'
                : undefined,
          ),
        ],
        { pullBottom: true },
      ),

      epc.subsections.subsection('content.list', 'List options', [
        epc.utils.observedField(
          epc.fields.listLayoutField('content.list.layout'),
          (model: ListPropsModel, { direction }: PropertiesConfigInputListLayoutModel) => {
            const oldDirection = model.content.list.layout.direction;
            if (oldDirection !== direction) {
              throw new Error('unsupported direction change');
            }
            return {
              'content.mobile.scrollMode':
                model.content.list.layout.mode === 'hscroll' ? 'swipe' : 'scroll',
            };
          },
        ),
        epc.fields.sortField(
          'content.sort',
          'Sort by',
          'content.columns',
          'name',
          COLUMN_ID_OR_NAME,
        ),
        ...epc.fields.emptyStateFields({
          messagePath: 'content.noDataMessage',
        }),
      ]),
      epc.subsections.subsection(
        'content.mobile',
        'Mobile',
        [
          epc.fields.radiosField('content.mobile.scrollMode', 'List behavior', [
            { value: ListScrollMode.Scroll, label: 'Scroll ' },
            { value: ListScrollMode.Swipe, label: 'Swipe' },
          ]),
        ],
        {
          hideWhen: (elementId, subsectionResolver, props: ListPropsModel) => {
            return props.content.list.layout.direction === 'notification';
          },
        },
      ),
      epc.subsections.searchAndFiltersSubsection(),

      buildActionsSubsection(),

      epc.subsections.subsection(
        'content.indicator',
        'Icon indicator',
        [
          epc.fields.iconField('content.indicator.icon', 'Icon'),
          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.indicator.color', 'Color', {
                allowGradient: false,
                allowNone: false,
              }),
              epc.fields.sliderField('style.indicator.size', 'Size', [
                { min: 8, max: 48, unit: 'px' },
              ]),
              epc.fields.alignField('content.indicator.align', 'Align', {
                vertical: true,
                excludedOptions: ['center'],
              }),
            ],
            'content.indicator.icon',
            (value?: string) => isNullish(value),
          ),
        ],
        { optional: true },
      ),

      epc.subsections.subsection('content.container', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
        ),
      ]),
      epc.subsections.visibilitySubsection(),
      epc.subsections.titleContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.list', 'Padding & Margin', [
          epc.fields.spacingAndAlignField('style.list.offset', {
            hasMargin: true,
            hasPadding: true,
          }),
        ]),
        epc.subsections.elementBackgroundSubsection('style.background', 'Card', {
          hoverPath: 'style.list.hover',
          hideItemHover: (_, props: PropsOf<'list'>, { hasRelatedElements }) =>
            props.content.rowClick?.enabled !== true && !hasRelatedElements,
          selectedRowPath: 'style.list.selectedRow',
          hideItemSelected: (_, __, { hasRelatedElements }) => !hasRelatedElements,
          extraConfigItems: [
            epc.fields.colorField('style.general.description', 'Description', {
              allowNone: false,
              allowGradient: false,
            }),
            epc.fields.colorField('style.general.actions', 'Actions', {
              allowNone: false,
              allowGradient: false,
            }),
            epc.fields.colorField('style.general.accent', 'Accent', {
              allowNone: false,
              allowGradient: false,
            }),
          ],
        }),
        epc.subsections.elementBackgroundSubsection('style.container', 'General', {
          hideEffectPanel: true,
        }),
        conditionalFormattingSubsection(),
        epc.subsections.titleStyleSubsection({
          enabled: 'content.title.enabled',
          color: 'style.general.heading',
          fontFamily: 'style.titleBlock.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'style.titleBlock.fontStyle',
          fontSize: 'style.titleBlock.fontSize',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.titleBlock.iconSize',
        }),
      ],
      true,
    ),
  ]);
}

function buildActionsSubsection(): PropertiesConfigSubsection {
  const commonActionsHeader: PropertiesConfigItemDefinition[] = [
    epc.utils.adornField<'toggle', PropertiesConfigInputOptionsToggle>(
      epc.fields.toggleField('content.itemAdd.enabled', 'Add new item'),
      {
        action: () => ({
          id: ITEMS_CONTAINER_ACTION_ADD,
          styled: { enabled: true, elementType: 'button' },
        }),
      },
    ),
    epc.utils.conditionalField(
      epc.fields.textInput('content.itemAdd.label', 'Add label'),
      'content.itemAdd.enabled',
      (value) => !value,
    ),
    ...epc.fields.containerRowClickActionsField('On item click', 'Edit item'),
  ];
  const rowActionsGroup: PropertiesConfigItemDefinition[] = [
    epc.fields.unifiedActionsField('content.unifiedRowActions', 'row', {
      hasWorkflow: (action: PropertiesConfigInputContainerItemUnifiedRowActionModel) =>
        action.type === 'delete' || action.type === 'custom',
      workflowIdPath: (action: PropertiesConfigInputContainerItemUnifiedRowActionModel) =>
        action.type === 'delete' ? 'content.itemDelete.targetId' : undefined,
    }),
    ...epc.utils.conditionalFields(
      [
        epc.utils.separatedField(
          epc.fields.contentAlignField('content.actions.align', 'Align', { hideLabel: true }),
          { label: 'Show Actions', hideSeparatorWhenHidden: true },
        ),
        epc.fields.actionsDisplayModeField('content.actions.mode', 'Display mode', {
          hideLabel: true,
        }),
      ],
      'content.unifiedRowActions',
      (actions: PropertiesConfigInputContainerItemActionModel[], model: ListPropsModel) =>
        !actions?.length ||
        actions.every((item) => !(item.label || item.icon)) ||
        model.content.list.layout.direction === 'subscription-plan' ||
        model.content.list.layout.direction === 'e-commerce',
    ),
  ];
  const bulkActionsGroup: PropertiesConfigItemDefinition[] = [
    epc.fields.unifiedActionsField('content.unifiedBulkActions', 'bulk', {
      hasWorkflow: (action: PropertiesConfigInputContainerItemUnifiedBulkActionModel) =>
        action.type === 'export' || action.type === 'custom',
      workflowIdPath: (action: PropertiesConfigInputContainerItemUnifiedBulkActionModel) =>
        action.type === 'export' ? 'content.bulkExport.targetId' : undefined,
    }),
    ...epc.utils.conditionalFields(
      [
        epc.utils.separator('Show Actions'),
        epc.fields.actionsDisplayModeField(
          'content.list.bulkActionsDisplayMode',
          '',
          { hideLabel: true, leftIndent: true },
          true,
        ),
      ],
      'content',
      (content: WithUnifiedBulkActions) => content.unifiedBulkActions.length === 0,
    ),
  ];

  return epc.subsections.subsection(
    'content.actions',
    'Actions',
    [
      ...commonActionsHeader,
      epc.fields.radiosField(
        'content.list.actionsView',
        '',
        [
          {
            value: 'row',
            label: 'Row',
            badge: (props: ListPropsModel) =>
              props.content.unifiedRowActions.length === 0
                ? undefined
                : props.content.unifiedRowActions.length.toString(),
          },
          {
            value: 'bulk',
            label: 'Bulk',
            badge: (props: ListPropsModel) =>
              props.content.unifiedBulkActions.length === 0
                ? undefined
                : props.content.unifiedBulkActions.length.toString(),
          },
        ],
        { hideLabel: true, leftIndent: true },
      ),
      ...epc.utils.disabledFields(
        [
          ...epc.utils.conditionalFields(
            rowActionsGroup,
            'content.list.actionsView',
            (actionsView) => actionsView !== 'row',
          ),
          ...epc.utils.conditionalFields(
            bulkActionsGroup,
            'content.list.actionsView',
            (actionsView) => actionsView !== 'bulk',
          ),
        ],

        (model: ListPropsModel & CanvasElementPropertiesWithData) =>
          !getValueByPath(model, 'data.?key:value.dataSource')
            ? 'The Value of the element is not bound'
            : undefined,
      ),
    ],
    {
      lockWhen: (model) =>
        !getValueByPath(model, 'data.?key:value.dataSource')
          ? 'The Value of the element is not bound'
          : undefined,
    },
  );
}

function blankListFormat(): PropertiesConfigInputListConditionalFormatModel {
  return {
    id: crypto.randomUUID(),
    name: '',
    fill: { color1: '#ffffff', selectorType: 'solid' },
    outline: { color1: '#ededed', selectorType: 'solid' },
    outlineWidth: '1px',
    headline: { color1: '#000000', selectorType: 'solid' },
    headlineWeight: '700',
    description: { color1: '#000000', selectorType: 'solid' },
    conditions: { states: ['when'] },
  };
}

function conditionalFormattingSubsection(): PropertiesConfigSubsection {
  return epc.subsections.subsection(
    'content.conditionalFormatting',
    'Conditional Formatting',
    [epc.fields.listConditionalFormattingField('content.conditionalFormatting.formats')],
    {
      optional: true,
      tooltip:
        'Rules are evaluated from top to bottom for each list item. Only the first matching rule is applied.',
      itemAdd: (model: PropsOf<'list'>): { path: string; value: unknown } | undefined => {
        if (!model.content.conditionalFormatting?.enabled) {
          return;
        }
        return {
          path: 'content.conditionalFormatting.formats',
          value: [...(model.content.conditionalFormatting?.formats ?? []), blankListFormat()],
        };
      },
      lockWhen: (props: PropsOf<'list'>) =>
        getValueByPath(props, 'data.?key:value.dataSource')
          ? undefined
          : 'The Value of the element is not bound',
    },
  );
}
